import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  MoreHorizontal,
  Eye,
  Edit2,
  Shield,
  Key,
  CheckCircle,
  XCircle,
  Trash2,
  Cloud,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { nl } from 'date-fns/locale'
import type { User } from '@/lib/types'
import { useHasPermission } from '@/lib/hooks/usePermission'

interface UserCardProps {
  user: User
  currentUser: User | null
  getRoleBadgeColor: (role: string) => string
  onViewClick: (user: User) => void
  onEditClick: (user: User) => void
  onRoleClick: (user: User) => void
  onChangePasswordClick: (user: User) => void
  onToggleActive: (user: User) => void
  onDeleteClick: (user: User) => void
  isChangingPassword: boolean
  isDeleting: boolean
}

export function UserCard({
  user,
  currentUser,
  getRoleBadgeColor,
  onViewClick,
  onEditClick,
  onRoleClick,
  onChangePasswordClick,
  onToggleActive,
  onDeleteClick,
  isChangingPassword,
  isDeleting,
}: UserCardProps) {
  const { t } = useTranslation()

  // Permission-based access control
  const canManageUsers = useHasPermission('users.manage')
  const canManagePasswords = useHasPermission('users.manage.password')
  const canDeleteUsers = useHasPermission('users.delete')

  // Check if current user can edit this user
  // Super admins can edit anyone
  // Regular admins can't edit super admins or other admins with higher/equal privilege
  const canEdit =
    currentUser?.isSuperAdmin ||
    (canManageUsers &&
      !user.isSuperAdmin &&
      !user.roles?.some(r => r.slug === 'admin' || r.slug === 'super_admin'))

  // Check if this is an Azure-managed user
  // Azure users are managed by Entra ID and should not be edited locally
  const isAzureUser = !!user.azureObjectId

  // Super admins can delete Azure users, regular admins cannot
  const canDeleteAzureUser = currentUser?.isSuperAdmin

  return (
    <div className="flex md:items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
      <div className="flex items-center space-x-4 flex-1">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">
              {user.displayName || `${user.firstName} ${user.lastName}`}
            </p>
            {/* Azure user indicator */}
            {isAzureUser && (
              <Badge variant="secondary" className="text-xs flex items-center gap-1">
                <Cloud className="h-3 w-3" />
                {t('admin.users.azureManaged', 'Azure Managed')}
              </Badge>
            )}
            {/* Display RBAC roles */}
            {user.roles && user.roles.length > 0 && (
              user.roles.map(role => (
                <Badge key={role.id} className={getRoleBadgeColor(role.slug)}>
                  {role.name}
                </Badge>
              ))
            )}
            {!user.isActive && (
              <Badge variant="outline" className="text-xs">
                {t('admin.users.inactive')}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-2">{user.email}</p>
          <div className="flex mt-2">
            {user.lastLoginAt && (
              <span className="text-xs text-muted-foreground mt-1">
                {t('admin.users.lastLogin')}:{' '}
                {formatDistanceToNow(new Date(user.lastLoginAt), {
                  addSuffix: true,
                  locale: nl,
                })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Show actions for non-Azure users, or for Azure users if current user is super admin */}
      {!isAzureUser || canDeleteAzureUser ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="secondary" size="sm" className="mt-4 md:mt-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-1" align="end">
            <div className="flex-col flex items-stretch justify-start space-y-2">
              <Button
                variant="ghost"
                size="sm"
                className="justify-start"
                onClick={() => onViewClick(user)}
              >
                <Eye className="h-4 w-4 mr-2" />
                {t('admin.users.view')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="justify-start"
                onClick={() => onEditClick(user)}
                disabled={!canEdit || isAzureUser}
              >
                <Edit2 className="h-4 w-4 mr-2" />
                {t('admin.users.edit')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="justify-start"
                onClick={() => onRoleClick(user)}
                disabled={!canEdit || isAzureUser}
              >
                <Shield className="h-4 w-4 mr-2" />
                {t('admin.users.changeRole')}
              </Button>
              {canManagePasswords && canEdit && !isAzureUser && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  onClick={() => onChangePasswordClick(user)}
                  disabled={isChangingPassword}
                >
                  <Key className="h-4 w-4 mr-2" />
                  {t('admin.users.changePassword')}
                </Button>
              )}

              {canManageUsers && canEdit && !isAzureUser && (
                <Button
                  variant={user.isActive ? 'destructive' : 'default'}
                  size="sm"
                  className="justify-start"
                  onClick={() => onToggleActive(user)}
                >
                  {user.isActive ? (
                    <XCircle className="h-4 w-4 mr-2" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {user.isActive ? t('admin.users.deactivate') : t('admin.users.activate')}
                </Button>
              )}
              {/* Super admins can delete Azure users, regular admins cannot */}
              {canDeleteUsers && (isAzureUser ? canDeleteAzureUser : canEdit) && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="justify-start"
                  onClick={() => onDeleteClick(user)}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('admin.users.delete')}
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      ) : null /* Azure users without super admin access - should not reach here due to condition above */}
    </div>
  )
}
